import React, { useState } from 'react';
import { Building2, Copy, CheckCircle2 } from 'lucide-react';

interface PaymentInstructionsProps {
  selectedPlanPrice: number;
}

export const PaymentInstructions: React.FC<PaymentInstructionsProps> = ({ 
  selectedPlanPrice 
}) => {
  const [copied, setCopied] = React.useState(false);

  const bankDetails = {
    bankName: 'BRED Bank',
    accountName: 'Innovatelhub Solutions Ltd',
    accountNumber: '013307727100019',
    swiftCode: 'BREDVUVUXXX',
    reference: 'SUBSCRIPTION',
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
      <div className="flex items-center gap-3 mb-6">
        <Building2 className="w-8 h-8 text-blue-600" />
        <h3 className="text-2xl font-bold text-gray-900">Bank Transfer Details</h3>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex justify-between items-center py-3 border-b border-gray-100">
          <span className="text-gray-600 font-medium">Bank Name</span>
          <span className="text-gray-900 font-semibold">{bankDetails.bankName}</span>
        </div>

        <div className="flex justify-between items-center py-3 border-b border-gray-100">
          <span className="text-gray-600 font-medium">Account Name</span>
          <span className="text-gray-900 font-semibold">{bankDetails.accountName}</span>
        </div>

        <div className="flex justify-between items-center py-3 border-b border-gray-100">
          <span className="text-gray-600 font-medium">Account Number</span>
          <div className="flex items-center gap-2">
            <span className="text-gray-900 font-semibold font-mono">
              {bankDetails.accountNumber}
            </span>
            <button
              onClick={() => handleCopy(bankDetails.accountNumber)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Copy account number"
            >
              {copied ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center py-3 border-b border-gray-100">
          <span className="text-gray-600 font-medium">SWIFT Code</span>
          <span className="text-gray-900 font-semibold font-mono">{bankDetails.swiftCode}</span>
        </div>

        <div className="flex justify-between items-center py-3">
          <span className="text-gray-600 font-medium">Payment Reference</span>
          <span className="text-blue-600 font-semibold font-mono">
            {bankDetails.reference}
          </span>
        </div>
      </div>

      <div className="mt-6 bg-blue-100 rounded-lg p-4">
        <p className="text-sm text-blue-900 font-medium mb-2">Amount to Transfer:</p>
        <p className="text-3xl font-bold text-blue-600">
          {selectedPlanPrice.toLocaleString()} VT
        </p>
      </div>

      <div className="mt-6 space-y-3">
        <h4 className="font-semibold text-gray-900">Important Instructions:</h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold mt-0.5">1.</span>
            <span>Transfer the exact amount to the account above</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold mt-0.5">2.</span>
            <span>Include the payment reference in your transfer</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold mt-0.5">3.</span>
            <span>Upload your payment receipt on the next screen</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold mt-0.5">4.</span>
            <span>Your subscription will activate once payment is approved by admin</span>
          </li>
        </ul>
      </div>

      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <span className="font-semibold">Note:</span> Processing time typically takes 1-2
          business days. You will receive an email notification once your payment is approved.
        </p>
      </div>
    </div>
  );
};